const n = (one, many) => ({ n }) => (n === 1 ? one.replace("{n}", n) : many.replace("{n}", n));

const en = {
  // Counts
  nights: n("{n} night", "{n} nights"),
  adults: n("{n} adult", "{n} adults"),
  children: n("{n} child", "{n} children"),
  rooms: n("{n} room", "{n} rooms"),
  beds: n("{n} bed", "{n} beds"),
  upToGuests: n("Up to {n} guest", "Up to {n} guests"),
  roomTypesAvailable: n("{n} room type available", "{n} room types available"),
  photosCount: n("{n} photo", "{n} photos"),
  rateOptions: n("{n} rate option", "{n} rate options"),

  // Top bar, footer, language
  "nav.signIn": "Sign in",
  "nav.home": "{name} home",
  "lang.title": "Choose a language",
  "lang.label": "Language",
  "footer.rights": "All rights reserved",
  "footer.bookingBy": "Booking by",
  "footer.poweredBy": "Powered by",

  // Hero
  "hero.welcome": "Welcome to {name}",
  "hero.welcomePlain": "Welcome",
  "hero.photos": "Hero photos",
  "hero.photo": "Photo {n}",

  "group.eyebrow": "Our hotels",
  "group.title": "Choose where to stay",
  "group.count": ({ n }) => (n === 1 ? "1 hotel. Pick it to see rooms and rates." : `${n} hotels. Pick one to see its rooms and rates.`),
  "group.empty": "No hotels are listed yet.",
  "group.visit": "See rooms & book",

  "home.roomsEyebrow": "Rooms & rates",
  "home.factsEyebrow": "In our rooms",
  "home.factsTitle": "What you'll find here",
  "home.galleryEyebrow": "Gallery",
  "home.galleryTitle": "Take a look around",
  "home.morePhotos": ({ n }) => (n === 1 ? "+1 photo" : `+${n} photos`),
  "home.locationEyebrow": "Location",
  "home.directions": "Get directions",
  "home.mapTitle": "Map showing {name}",

  // Search
  "search.label": "Find a room",
  "search.dates": "Check-in — Check-out",
  "search.guests": "Guests",
  "search.adults": "Adults",
  "search.children": "Children",
  "search.fewer": "Fewer {what}",
  "search.more": "More {what}",
  "search.chooseDates": "Choose dates",
  "search.chooseGuests": "Choose guests",
  "search.pickCheckout": "Now pick your check-out date",
  "search.done": "Done",
  "search.submit": "Search",

  "trust.direct": "Book direct with the hotel",
  "trust.secure": "Secure payment",
  "trust.email": "Confirmation sent by email",

  // Results
  "results.finding": "Finding rooms…",
  "results.choose": "Choose your room",
  "results.none": "No rooms available",
  "results.change": "Change search",
  "results.loadingAria": "Looking for available rooms",
  "err.availability": "Could not load availability.",
  "err.priced": "Rooms could not be priced. Please try again shortly.",

  "card.onlyLeft": "Only {n} left",
  "card.payAtHotel": "Pay at the hotel available",
  "card.from": "From",
  "card.perNight": "/ night",
  "card.total": "{total} for {nights} · incl. service charge",
  "card.seeRates": "See rates",
  "card.totalShort": "total for {nights}, incl. service charge",
  "card.more": "+{n} more",
  "media.soon": "Photos coming soon",

  "empty.title": "No rooms left for these dates",
  "empty.body": "Try different dates, or fewer guests per room.",
  "empty.change": "Change dates",

  // Room panel
  "panel.close": "Close",
  "panel.prev": "Previous photo",
  "panel.next": "Next photo",
  "panel.photoOf": "{name} ({n} of {total})",
  "panel.popular": "Popular amenities",
  "panel.offers": "Everything in this room",
  "panel.chooseRate": "Choose your rate",
  "panel.rateNote": "Prices are for the whole stay and include service charge.",
  "panel.rates": "Rates",
  "panel.roomRate": "Room rate",
  "panel.payAtHotel": "Pay at the hotel, nothing today",
  "panel.payNow": "Pay now",
  "panel.perNight": "{price} / night",
  "panel.rooms": "Rooms",
  "panel.fewerRooms": "Fewer rooms",
  "panel.moreRooms": "More rooms",
  "panel.total": "Total · {nights}",
  "panel.reserve": "Reserve",

  "fac.amenities": "Amenities",
  "fac.view": "View",
  "fac.bedroom": "Bedroom",
  "fac.bathroom": "Bathroom",
  "fac.entertainment": "Entertainment",
  "fac.kitchen": "Kitchen",
  "fac.room": "In the room",
  "fac.floor": "Floor",
  "fac.outdoor": "Outdoor",

  // Checkout
  "steps.label": "Booking progress",
  "steps.details": "Your details",
  "steps.payment": "Payment",
  "steps.confirmed": "Confirmed",
  "checkout.back": "Back to room",
  "checkout.title": "Almost there",
  "checkout.who": "Who's checking in?",
  "checkout.whoSub": "We'll send your confirmation here. The name should match the ID shown at check-in.",
  "checkout.rideum": ({ logo }) => <>Have a {logo} account? Sign in and we'll fill this in for you.</>,
  "form.firstName": "First name",
  "form.lastName": "Last name",
  "form.email": "Email",
  "form.mobile": "Mobile number",
  "form.emailPlaceholder": "name@example.com",
  "form.emailHint": "For your confirmation, and to cancel online.",
  "form.mobileHint": "So the hotel can reach you about your stay.",
  "form.firstNameReq": "Please enter your first name.",
  "form.lastNameReq": "Please enter your last name.",
  "form.emailInvalid": "Please enter a valid email address.",
  "form.contactMissing": "Please give us a mobile number or an email so the hotel can reach you.",
  "form.payNextTitle": "You'll pay on the next step",
  "form.payNextBody": "This rate is charged in full by secure payment. Your booking is confirmed once payment succeeds.",
  "form.payHotelTitle": "Nothing to pay today",
  "form.payHotelBody": "You pay the hotel when you check in. Your room is confirmed as soon as you finish.",
  "form.privacy": "Your details are only shared with the hotel.",
  "form.continuePay": "Continue to payment",
  "form.confirm": "Confirm booking",
  "err.booking": "Booking failed.",

  // Stay summary
  "sum.checkin": "Check-in",
  "sum.checkout": "Check-out",
  "sum.guests": "Guests",
  "sum.rooms": "Rooms",
  "sum.cancellation": "Cancellation",
  "sum.payment": "Payment",
  "sum.payAtHotel": "Pay at the hotel",
  "sum.payNow": "Pay now",
  "sum.roomsNights": "{rooms} × {nights}",
  "sum.extraAdults": "Extra adults ({n})",
  "sum.service": "Service charge ({pct}%)",
  "sum.total": "Total",
  "sum.idr": "Charged in IDR at today's rate.",
  "sum.bookingFor": "Booking for {name}",

  // Payment
  "pay.back": "Back to your details",
  "pay.title": "Pay securely",
  "pay.loadError": "We couldn't load the secure payment form. Please refresh and try again.",
  "pay.waitingTitle": "We're waiting for your payment",
  "pay.waitingBody": "Your room is held for {min} minutes from when you started. Follow the payment instructions you were given — after that the payment can no longer be accepted and the room is released. We'll email you as soon as your payment is confirmed, so you can safely close this page.",
  "pay.hi": "Hi {name}, one last step",
  "pay.sub": "Press Pay to choose how you'd like to pay. Your booking is confirmed as soon as the payment goes through.",
  "pay.pay": "Pay {amount}",
  "pay.retry": "Try again {amount}",
  "pay.loading": "Loading secure form…",
  "pay.secure": "Payments are processed securely by Midtrans. We never see your card details.",
  "pay.errConfig": "Could not load payment configuration.",
  "pay.errNotReady": "The payment form is still loading. Please try again in a moment.",
  "pay.errFailed": "Payment failed.",
  "pay.errDeclined": "That payment did not go through. You can try again.",
  "pay.errOpen": "The payment form could not be opened. Please try again.",

  "loader.title": "Please wait a moment",
  "loader.body": "We're processing your request. Please don't close this window or leave this page.",
  "loader.loading": "Loading…",

  // Confirmation
  "conf.loading": "Loading your booking…",
  "conf.notFoundTitle": "We couldn't find this booking",
  "conf.notFoundBody": "Check the link in your confirmation email, or contact the hotel with your booking number.",
  "conf.backHotel": "Back to the hotel",
  "conf.theHotel": "the hotel",
  "conf.booked": ({ name }) => (name ? `You're booked, ${name}!` : "You're booked!"),
  "conf.leadEmail": ({ hotel, email }) => <>Your stay at <strong>{hotel}</strong> is confirmed. We've sent the details to <strong>{email}</strong>.</>,
  "conf.leadNoEmail": ({ hotel }) => <>Your stay at <strong>{hotel}</strong> is confirmed. Keep your booking number handy for check-in.</>,
  "conf.waitTitle": "We're waiting for your payment",
  "conf.waitLead": ({ email }) => <>Your room is held while your payment comes through.{email ? <> We'll email <strong>{email}</strong> as soon as it's confirmed.</> : null}</>,
  "conf.cancelTitle": "This booking is cancelled",
  "conf.cancelLead": ({ hotel }) => <>Your stay at <strong>{hotel}</strong> will not go ahead.</>,
  "conf.bookingNo": "Booking no.",
  "conf.copy": "Copy",
  "conf.copied": "Copied",
  "conf.copyAria": "Copy booking number",
  "conf.guest": "Guest",
  "conf.roomsGuests": "Rooms · guests",
  "conf.email": "Email",
  "conf.mobile": "Mobile",
  "conf.policy": "Cancellation policy",
  "conf.showAtDesk": "Show this page or your booking number at the front desk when you arrive.",
  "conf.print": "Print",
  "print.title": "Booking Confirmation",
  "print.bookingNo": "Booking no.",
  "print.guest": "Guest",
  "print.email": "Email",
  "print.phone": "Phone",
  "print.rooms": "Rooms",
  "print.adults": "Adults",
  "print.children": "Children",
  "print.roomType": "Room type",
  "print.policy": "Cancellation policy",
  "print.arrival": "Arrival",
  "print.departure": "Departure",

  // Guest cancellation
  "cx.cancelledTitle": "This booking has been cancelled.",
  "cx.cancelledBody": "Any refund due follows the cancellation policy above. The hotel arranges it directly and will be in touch — it is not returned automatically to your card.",
  "cx.doneTitle": "Your booking has been cancelled.",
  "cx.refundDue": "A refund of {amount} is due to you. The hotel will arrange it directly and be in touch about where to send it.",
  "cx.noRefund": "No refund is due under the cancellation policy.",
  "cx.plansChanged": "Plans changed?",
  "cx.cancelLink": "Cancel this booking",
  "cx.title": "Cancel this booking",
  "cx.intro": "Confirm the email address or mobile number the booking was made under and we'll show you what cancelling costs. Nothing is cancelled yet.",
  "cx.placeholder": "you@example.com or +62…",
  "cx.checking": "Checking…",
  "cx.showTerms": "Show cancellation terms",
  "cx.fee": "Cancellation fee",
  "cx.refund": "Refund due to you",
  "cx.outstanding": "Still payable to the hotel",
  "cx.irreversible": "This cannot be undone. Any refund is arranged by the hotel, not returned automatically to your card.",
  "cx.cancelling": "Cancelling…",
  "cx.confirm": "Confirm cancellation",
  "cx.keep": "Keep my booking",
  "cx.generic": "Something went wrong. Please try again.",
  "cx.FullyRefundable": "Free cancellation",
  "cx.PartiallyRefundable": "Partially refundable",
  "cx.NonRefundable": "Non-refundable",

  // Rideum account
  "auth.signIn": "Sign in",
  "auth.create": "Create account",
  "auth.loading": "Loading…",
  "auth.what": "Your Rideum account saves your details, so booking is quicker next time.",
  "login.title": "Sign in to Rideum",
  "login.sub": "Use the email or mobile number on your Rideum account.",
  "login.emailOrMobile": "Email or mobile number",
  "login.placeholder": "name@example.com or 0812…",
  "login.required": "Please enter your email or mobile number.",
  "login.invalid": "That email or mobile number isn't valid.",
  "login.continue": "Continue",
  "login.mobileNotFound": "No Rideum account uses the mobile number {phone}.",
  "login.resetDone": "Your password has been reset. Please check your email.",
  "login.forgotTitle": "Forgot your password?",
  "login.forgotSub": "Enter your Rideum email and we'll send you a new one.",
  "login.registeredEmail": "Rideum account email",
  "login.emailRequired": "Please enter your email.",
  "login.emailInvalid": "That email address isn't valid.",
  "login.sendReset": "Send new password",
  "login.backToSignIn": "Back to sign in",
  "login.verifyTitle": "Verify your Rideum account",
  "login.verifyEmail": () => <>We've emailed you a <strong>temporary password</strong>. Use it to sign in.</>,
  "login.welcome": "Welcome back to Rideum",
  "login.password": "Password",
  "login.passwordPlaceholder": "Your Rideum password",
  "login.passwordRequired": "Please enter your password.",
  "login.forgot": "Forgot password?",
  "login.signIn": "Sign in",
  "login.back": "Back",
  "login.mobileTitle": "Sign in with your mobile",
  "login.sms": () => <>We've sent you an SMS. Enter the <strong>4-digit code</strong> to continue.</>,
  "login.verifySignIn": "Verify and sign in",
  "login.changeMethod": "Use a different email or number",
  "otp.expired": "Code expired. Send a new one",
  "otp.expiresIn": "Code expires in {time}",

  "reg.title": "Create a Rideum account",
  "reg.sub": "Free, and it saves your details for next time.",
  "reg.withEmail": "Email",
  "reg.withMobile": "Mobile",
  "reg.emailReq": "Please enter your email.",
  "reg.emailInvalid": "That email address isn't valid.",
  "reg.emailHint": "You'll sign in with this, and we'll email your password here.",
  "reg.mobileHint": "You'll sign in with this, using a code we send by SMS.",
  "reg.agree": ({ terms, privacy }) => <>By creating an account you agree to the {terms} and {privacy}.</>,
  "reg.create": "Create Rideum account",
  "reg.have": "Already have a Rideum account?",
  terms: "Terms & Conditions",
  "url.terms": "https://www.rideum.io/en/legal/terms",
  "url.privacy": "https://www.rideum.io/en/legal/privacy",
  privacy: "Privacy Policy",

  "prof.fullName": "Full name",
  "prof.email": "Email",
  "prof.mobile": "Mobile",
  "prof.notProvided": "Not provided",
  "prof.birthday": "Birthday",
  "prof.addBirthday": "Not added",
  "prof.account": "Account",
  "prof.guest": "Guest",
  "prof.changePassword": "Change password",
  "prof.signOut": "Sign out",
  "prof.close": "Close",

  "pw.title": "Change your password",
  "pw.sub": "Choose a password you don't use anywhere else.",
  "pw.current": "Current password",
  "pw.currentReq": "Please enter your current password.",
  "pw.new": "New password",
  "pw.newPlaceholder": "At least 8 characters",
  "pw.newReq": "Please enter a new password.",
  "pw.min": "At least 8 characters.",
  "pw.mix": "Use at least 3 of: upper case, lower case, a number, a symbol.",
  "pw.update": "Update password",
  "pw.back": "Back to profile",
  "pw.changed": "Your password has been changed.",
};

export default en;
